// @flow
import React, { useEffect, useRef } from 'react';
import { get } from 'lodash';
import classNames from 'classnames';
import { keepKeysWithin } from '@helpers/helpers';
import ServiceService, { QUANTITY_INPUT_PATTERN, MAX_QUANTITY } from '../../../services/ServiceService';
import './QuantityStepper.scss';

type Props = {
  value: string,
  onChange: (value: string) => void,
  invalid?: boolean,
  inputLabel?: string,
  decreaseLabel?: string,
  increaseLabel?: string,
};

const QuantityStepper = ({
  value, onChange, invalid, inputLabel, decreaseLabel, increaseLabel,
}: Props) => {
  const quantity = ServiceService.parseQuantity(value);
  const current = Number.isFinite(quantity) ? quantity : 0;
  const root = useRef(null);

  // Space would toggle the row the stepper sits in, and Enter on − or + would save the form before the step
  useEffect(() => keepKeysWithin(root.current, (event) => (
    event.key === ' ' || (event.key === 'Enter' && get(event, ['target', 'tagName']) === 'BUTTON')
  )), []);

  const step = (delta: number) => () => onChange(ServiceService.quantityInputValue(current + delta));

  const handleChange = (event: Object) => {
    const text = event.target.value;
    if (QUANTITY_INPUT_PATTERN.test(text)) {
      onChange(text);
    }
  };

  return (
    <div className={classNames('quantityStepper', { invalid })} ref={root}>
      <button type="button" aria-label={decreaseLabel} disabled={current <= 1} onClick={step(-1)}>−</button>
      <input
        type="text"
        inputMode="decimal"
        aria-label={inputLabel}
        aria-invalid={!!invalid}
        value={value}
        onChange={handleChange}
        onFocus={(event) => event.target.select()}
        autoComplete="off"
      />
      <button type="button" aria-label={increaseLabel} disabled={current + 1 > MAX_QUANTITY} onClick={step(1)}>+</button>
    </div>
  );
};

export default QuantityStepper;
